export type RoundTransitionAction = 'clearAll' | 'carryForward' | 'rebet'

/**
 * What to do with the chip board when a round transitions (`race_ready`).
 *
 * - `clearAll`   — auto-bet off AND a bet was submitted last round: that bet is
 *                  done, so wipe the board for a fresh round.
 * - `carryForward` — auto-bet off AND nothing was submitted last round: the chips
 *                  on the board are unfinished placements QUEUED for the next
 *                  round (placed while betting was closed). Keep everything —
 *                  chips, free bets and boosters — so they submit at the next
 *                  round start exactly as placed. (Cash chips already survived
 *                  here; free-bet chips must too, otherwise a free bet placed
 *                  during an in-progress round silently vanishes with no /bet.)
 * - `rebet`      — auto-bet on: re-bet last round's board, stripping any free-bet
 *                  chip (its free bet was consumed by RegisterBet) and re-binding
 *                  boosters to the surviving cash chips.
 *
 * The auto-bet insufficient-balance guard is handled by the caller before this.
 */
export function roundTransitionAction(params: {
  autoBet: boolean
  hadSuccessfulSubmission: boolean
}): RoundTransitionAction {
  if (!params.autoBet) {
    return params.hadSuccessfulSubmission ? 'clearAll' : 'carryForward'
  }
  return 'rebet'
}

/**
 * True when the `race_ready` round reset is being re-run INSIDE a hard round
 * by the round's own bets, not by a new round opening (hard close rev 4,
 * DESIGN §6).
 *
 * The reset is an effect gated on the latest row being `race_ready`, and it
 * also re-runs whenever the state it reads changes (`betsActuallySent`,
 * `autoBet`). A round that is not hard sends 1.5 s into `race_starting`, so the
 * latest row is never `race_ready` when that state changes. A hard round
 * sends at its lock, and its countdown usually locks 20 ms to 1 s BEFORE
 * `race_starting` reaches the page: the send's `betsActuallySent` (and a board
 * rebuilt from the round's accepted bets, which sets it too) then re-ran the
 * reset as if the next round had opened. It cleared the board and unlocked
 * it while the bet stood on the server: the option looked empty and took
 * chips, and a chip laid then rode into the next round as a new bet
 * (staging 10-27, Survivor).
 *
 * That re-run must change nothing: the board holds this round's bets, locked,
 * until the next round's `race_ready`. Its own bets are the ones its lock sent
 * (`submittedRoundId`) or the ones it was rebuilt from (`restoredRoundId`). A
 * round that is not hard is never matched, so its reset runs exactly as it did.
 */
export function isOwnRoundRerun(params: {
  hard: boolean
  currentRoundId: string | undefined
  submittedRoundId: string | undefined
  restoredRoundId: string | undefined
}): boolean {
  if (!params.hard || params.currentRoundId === undefined) return false
  return (
    params.submittedRoundId === params.currentRoundId ||
    params.restoredRoundId === params.currentRoundId
  )
}
